import React, { useCallback, useMemo, useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { DashboardSmallUser } from 'api/models/DashboardSmallUser';
import { UserProfile } from 'api/models/UserProfile';
import { ApiService } from 'api/services/ApiService';
import { userSearchQueryKey, userSearchStaleTime } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { useDebouncedValue } from 'hooks/useDebouncedValue';

const toUserProfile = (user: DashboardSmallUser): UserProfile => ({
  id: user.id,
  first_name: user.first_name,
  last_name: user.last_name,
  email: user.email,
});

const getUserDisplayName = (user: UserProfile): string =>
  user.first_name ? `${user.first_name} ${user.last_name ?? ''}`.trim() : user.email || 'Unknown User';

export type StaffPreparerPickerProps = {
  /** Initial staff shown in the list (same pattern as AssignUsersDialog `availableUsers.preparers`). */
  seedUsers?: UserProfile[];
  selectedPreparers: UserProfile[];
  onSelectedPreparersChange: (users: UserProfile[]) => void;
  title?: string;
};

/**
 * Staff preparer multi-select using the same staff search API as ModelStatus AssignUsersDialog.
 */
const StaffPreparerPicker: React.FC<StaffPreparerPickerProps> = ({
  seedUsers = [],
  selectedPreparers,
  onSelectedPreparersChange,
  title = 'Preparers',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const { data: initialStaff = [] } = useQuery({
    queryKey: [userSearchQueryKey, 'initial-staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList(undefined);
      return response || [];
    },
    staleTime: userSearchStaleTime,
  });

  const { data: searchUsers = [], isLoading: isSearching } = useQuery({
    queryKey: [userSearchQueryKey, debouncedSearchTerm],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList(
        debouncedSearchTerm.length > 2 ? debouncedSearchTerm : undefined
      );
      return response || [];
    },
    enabled: debouncedSearchTerm.length > 2,
    staleTime: userSearchStaleTime,
  });

  const allAvailableUsers = useMemo(() => {
    const fromSeed = seedUsers;
    const fromInitial = initialStaff.map(toUserProfile);
    const fromSearch = searchUsers.map(toUserProfile);
    const combined = [...fromSeed, ...fromInitial, ...fromSearch];
    const seen = new Set<number>();
    return combined.filter(u => {
      if (!u.id || seen.has(u.id)) {
        return false;
      }
      seen.add(u.id);
      return true;
    });
  }, [seedUsers, initialStaff, searchUsers]);

  const filteredUsers = useMemo(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.length < 3) {
      return allAvailableUsers;
    }
    const searchLower = debouncedSearchTerm.toLowerCase();
    return allAvailableUsers.filter(user => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const email = user.email?.toLowerCase() || '';
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [allAvailableUsers, debouncedSearchTerm]);

  const handlePreparerToggle = useCallback(
    (user: UserProfile) => {
      onSelectedPreparersChange(
        selectedPreparers.some(u => u.id === user.id)
          ? selectedPreparers.filter(u => u.id !== user.id)
          : [...selectedPreparers, user]
      );
    },
    [onSelectedPreparersChange, selectedPreparers]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: selectedPreparers.length ? 1.5 : 2 }}>
        <PersonIcon />
        <Typography variant="subtitle1">{title}</Typography>
      </Box>

      {selectedPreparers.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
          {selectedPreparers.map(user => (
            <Chip
              key={user.id}
              size="small"
              label={getUserDisplayName(user)}
              onDelete={() => handlePreparerToggle(user)}
              color="primary"
              variant="outlined"
            />
          ))}
        </Stack>
      )}

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search staff members..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        size="small"
        sx={{ mb: 2 }}
        helperText="Enter at least 3 characters to search staff; initial list loads automatically."
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      <List sx={{ maxHeight: 360, overflow: 'auto' }}>
        {isSearching && (
          <ListItem>
            <ListItemText
              primary="Searching staff members..."
              secondary={
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">Please wait</Typography>
                </Box>
              }
            />
          </ListItem>
        )}
        {!isSearching && filteredUsers.length === 0 && (
          <ListItem>
            <ListItemText primary="No staff members found" secondary="Try a different search term" />
          </ListItem>
        )}
        {!isSearching &&
          filteredUsers.map(user => (
            <ListItem
              key={user.id}
              divider
              secondaryAction={
                <Box display="flex" alignItems="center">
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    Preparer
                  </Typography>
                  <Checkbox
                    edge="end"
                    checked={selectedPreparers.some(u => u.id === user.id)}
                    onChange={() => handlePreparerToggle(user)}
                    color="secondary"
                  />
                </Box>
              }>
              <ListItemText
                primary={getUserDisplayName(user)}
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                }
              />
            </ListItem>
          ))}
      </List>
    </Paper>
  );
};

export default StaffPreparerPicker;
